















































































import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import iwDuration from "@/encoding/pages/questions/pricing/Duration.vue";
import iwPricingPack from "@/optimizedEncoding/components/packSelection/PackPricing.vue";
import iwResultsGuaranteeNotice from "@/shared/components/pricing/ResultsGuaranteeNotice.vue";
import iwPackPreview from "@/optimizedEncoding/components/packSelection/packPreview/PackPreview.vue";
import iwNextPreviousButton from "@/encoding/components/NextPreviousButton.vue";
import iwPackInfoModal from "@/shared/components/pricing/PackInfoModal.vue";
import { updateClassified, validationClassified } from "@/shared/scripts/ValidateData";
import { mapActions } from "vuex";
import { getPricing } from "@/shared/scripts/PricingApi";
import { pushPageViewEvent } from "@/shared/store/modules/gtm/datalayer";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";
import { WizardState } from "@/shared/interfaces/WizardState.interface";

import { isEmpty } from "lodash";
import { configurePacksOnInit } from "@/optimizedEncoding/scripts/packConfig";
import { OptimizedPack } from "@/shared/interfaces/optimizedEncoding/PackSelection.interface";
import { hasValidBillboardBillingAddress, isBillboardSelected } from "@/shared/scripts/helpers/customer/billingAddress";
import { OrderApiError } from "@/shared/interfaces/order/Order.interface";
import missingCustomerNameMixin from "@/shared/mixins/missingCustomerName";


export default missingCustomerNameMixin.extend({

    components: {
        iwResultsGuaranteeNotice: iwResultsGuaranteeNotice,
        iwDuration: iwDuration,
        iwPricingPack: iwPricingPack,
        iwPackPreview: iwPackPreview,
        iwNextPreviousButton: iwNextPreviousButton,
        iwPackInfoModal: iwPackInfoModal
    },

    props: {
        action: {
            type: String,
            default: null,
            required: false
        }
    },

    data: function() {
        return {
            pathInStore: "transaction.pricing",
            selectedPackageInStore: "publication.visualizationOption",
            configuredPacksInStore: "transaction.configuredPacks",
            isButtonDisabled: false as boolean,
            validationChecking: false,
            packsLoaded: false as boolean,
            checkboxResetVersion: 0,
            packInfoType: null as string | null,
        };
    },

    computed: {
        ...mapState("FormModule", ["copyFromId"]),
        ...mapGetters({
            hasErrors: "FormModule/hasErrors",
            getValueTransaction: "FormModule/getValueTransaction",
        }),
        ...mapState("StepsWizard", {
            currentStep: state => (state as WizardState).current,
        }),
        packs: function(): Record<string, Record<string, unknown>> {
            return this.$store.getters["FormModule/getValueTransaction"](this.pathInStore);
        },
        getDiscountId: function(): string|null {
            const discount = this.$store.getters["FormModule/getValueUI"]("transaction.pack.automaticDiscount");
            if (discount?.id && discount.value < 0) {
                return discount.id;
            }
            return null;
        },
        discountTitle: function(): string {
            if (!this.getDiscountId) return "";
            const titleKey = this.getDiscountId.toLowerCase() + "_title";
            const discount = this.$store.getters["FormModule/getValueUI"]("transaction.pack.automaticDiscount");
            const params = { value: Math.abs(discount?.value || 0) };
            if (this.$te(titleKey)) {
                return this.$t(titleKey, params) as string;
            }
            return this.$t("automatic_discount_banner", params) as string;
        },
        getSelectedPackage: function(): string {
            return this.$store.getters["FormModule/getValueTransaction"](this.selectedPackageInStore);
        },
        hideAndDisablePreviousButton: function(): boolean {
            return this.currentStep.step === "extend-choice";
        },
        configuredPacks: function(): Array<OptimizedPack> {
            return this.$store.getters["FormModule/getValueTransaction"](this.configuredPacksInStore) || [];
        },
        shouldHideBillboard: function(): boolean {
            return this.action === "EXTENSION";
        },
        hasInvalidBillboardBillingAddress: function(): boolean {
            const hasBillboard = isBillboardSelected(
                this.$store.getters["FormModule/getValueTransaction"],
                this.$store.getters["FormModule/getValueOrder"]
            );

            if (!hasBillboard) {
                return false;
            }

            return !hasValidBillboardBillingAddress(this.$store.state.CustomersModule.currentCustomer);
        },
        totalPrice: function(): string {
            const selectedPack = this.getSelectedPackage;
            if (!selectedPack) return "0";

            const packOptions =
                this.getValueTransaction("optimizedEncoding.selectedPackOptions") || {};
            const extension = packOptions[selectedPack]?.extension;
            const billboardPrice = Number(packOptions[selectedPack]?.billboard?.price || 0);

            let packPrice;

            // When the extension is selected, the price for this pack is the full alternative
            // tariff price (base + this pack's extension), which is also what gets charged.
            // Only fall back to base + the basic-only scalar when per-pack data is unavailable.
            const extVizOption = extension?.visualizationOptions?.[selectedPack];
            if (extVizOption) {
                packPrice = Number(extVizOption.automaticDiscount != null ? extVizOption.totalPrice : extVizOption.price);
            } else {
                packPrice = Number(
                    this.$store.getters["FormModule/getValueTransaction"](
                        `optimizedEncoding.selectedPack.packPrice.${selectedPack}`
                    ) || 0
                ) + Number(extension?.price || 0);
            }

            const total = packPrice + billboardPrice;

            return this.formatCurrency(total.toString());
        },
    },

    watch: {
        getSelectedPackage: function(newPack: string) {
            if (!newPack) return;

            const packOptions =
                this.getValueTransaction("optimizedEncoding.selectedPackOptions") || {};

            if (packOptions[newPack]) {
                const extension = packOptions[newPack].extension ?? null;

                this.$store.commit("FormModule/addValueTransaction", {
                    path: "transaction.pricing.extensionOption",
                    value: extension
                });

                this.$store.commit(
                    "OrdersModule/setExtensionOption",
                    extension
                );
            }
        },
    },

    mounted: function() {
        pushPageViewEvent("classified_deposit_step12_package_selection");
    },

    created: function() {
        if(!this.configuredPacks.length) {
            this.getPacks();
        }

        // After a reload, the route guard restores currentCustomer as just { id }
        // (via getClassifiedId), so the billing address is missing and the billboard
        // billing-address check on "Next" wrongly fails. Hydrate the full customer
        // (incl. the provided address) the same way the payment OrderSummary does.
        const currentCustomer = this.$store.state.CustomersModule.currentCustomer;
        if (currentCustomer && !("hasInvoicingEnabled" in currentCustomer)) {
            this.setCurrentCustomer(currentCustomer.id)
                .catch(() => this.dialog.alert(i18n.t("server_not_responding_error").toString()));
        }
    },

    methods: {
        ...mapActions({
            goToPreviousStep: "StepsWizard/previous",
            goToNextStep: "StepsWizard/next",
        }),
        ...mapActions("OrdersModule", ["createOrder"]),
        ...mapActions("CustomersModule", ["setCurrentCustomer"]),

        pricingTypeRequired: function(): string {
            if(this.$props.action) {
                return this.$props.action;
            }
            return this.$store.state.FormModule.copyFromId ? "RENEWAL" : "PUBLICATION";
        },
        getPacks: async function(): Promise<void | null> {
            // Only price the classified currently in the funnel. `myClassifiedSelected` can hold a
            // stale classified from a previous publication/edit in the same session; if its id does
            // not match the funnel's current classified, ignore it and let getPricing read the live
            // form store (correct price + id). Otherwise getPricing would price the stale classified
            // and pick a tariff/discount for the wrong price range (RESTRICTED_TARIFF_PRICE_RANGE).
            const currentClassifiedId = this.$store.getters["FormModule/getClassifiedId"];
            const classified = this.$store.state.MyClassifiedsModule.myClassifiedSelected;
            const selectedClassified = (!isEmpty(classified) && classified.id === currentClassifiedId)
                ? classified
                : null;

            return await getPricing({
                classified: selectedClassified,
                type: this.pricingTypeRequired()
            })
                .then(() => {
                    const storedPacks = this.$store.getters["FormModule/getValueTransaction"](this.pathInStore);
                    if (storedPacks) {
                        const configured = configurePacksOnInit(storedPacks, this.getTransactionType(), {
                            hideBillboard: this.shouldHideBillboard
                        });
                        this.$store.commit("FormModule/addValueTransaction", {
                            "path": this.configuredPacksInStore,
                            "value": configured
                        });
                        this.packsLoaded = true;
                    }
                })
                .catch(error => {
                    this.dialog.alert(i18n.t("server_not_responding_error").toString());
                    console.error(error);
                    return null;
                });
        },
        clickNext: function(){
            if (this.hasInvalidBillboardBillingAddress) {
                this.dialog.alert(this.$t("billboard_complete_billing_address_required").toString());
                return;
            }

            // IO-154: both branches below create an order, which is what emits the B2C log to
            // Salesforce. Placed before either branch sets its in-flight flags, so the resets
            // here are belt-and-braces rather than load-bearing.
            if (this.blockOnMissingCustomerName(this.clickNext)) {
                this.isButtonDisabled = false;
                this.validationChecking = false;
                return;
            }

            if(this.currentStep.step === "extend-choice"){
                this.createOrder({
                    type: "EXTENSION",
                    successRoute: "extend-confirmation",
                    errorRoute: "extend-payment",
                }).then((res:boolean) => {
                    if (!res) {
                        this.validationChecking = false;
                        this.isButtonDisabled = false;
                        return false;
                    } else {
                        this.isButtonDisabled = true;
                        validationClassified((success:boolean) => {
                            Vue.nextTick(() => {
                                const inputsError = document.getElementsByClassName("input--error") as HTMLCollectionOf<HTMLElement>;
                                if (inputsError.length > 0) {
                                    inputsError[0].focus();
                                    window.scrollTo(0, 0);
                                }
                                if (success) {
                                    this.isButtonDisabled = false;
                                    this.goToNextStep();
                                } else {
                                    this.isButtonDisabled = false;
                                }
                            });
                        });
                    }
                }).catch((errors: Array<OrderApiError>) => {
                    this.isButtonDisabled = false;
                    if (this.openModalOnMissingCustomerNameError(errors, this.clickNext)) {
                        return;
                    }
                    this.dialog.alert(this.getOrderCreationErrorMessage(errors));
                });
            } else {
                this.isButtonDisabled = true;
                updateClassified((success:boolean) => {
                    Vue.nextTick(() => {
                        const inputsError = document.getElementsByClassName("input--error") as HTMLCollectionOf<HTMLElement>;
                        if (inputsError.length > 0) {
                            inputsError[0].focus();
                            window.scrollTo(0, 0);
                        }
                        if (success) {
                            this.createOrder().then((res:boolean) => {
                                if (!res) {
                                    this.validationChecking = false;
                                    this.isButtonDisabled = false;
                                    return false;
                                } else {
                                    this.isButtonDisabled = false;
                                    this.goToNextStep();
                                }
                            }).catch((errors: Array<OrderApiError>) => {
                                this.isButtonDisabled = false;
                                if (this.openModalOnMissingCustomerNameError(errors, this.clickNext)) {
                                    return;
                                }
                                this.dialog.alert(this.getOrderCreationErrorMessage(errors));
                            });
                        } else {
                            this.isButtonDisabled = false;
                        }
                    });
                });
            }
        },
        getOrderCreationErrorMessage: function(errors: Array<OrderApiError> = []): string {
            const hasBillingAddressError = errors.some((error: OrderApiError) =>
                error?.dataPath === "customer.location.address.provided"
            );

            if (hasBillingAddressError || this.hasInvalidBillboardBillingAddress) {
                return this.$t("billboard_complete_billing_address_required").toString();
            }

            return i18n.t("server_not_responding_error").toString();
        },
        openPackInfoModal: function(packType: string) {
            this.packInfoType = packType;
        },
        closePackInfoModal: function() {
            this.packInfoType = null;
        },
        clickPrevious: function(){
            this.goToPreviousStep();
        }
    }
});

